// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
// import { ButtonPresentationProps } from "@insite/mobius/Button"; // Store Custom Code
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import React, { FC, useState } from "react"; // Starter Custom Code
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import AddToCartOptionsAndButton, {
    AddToCartOptionsAndButtonStyles,
} from "@store/Library/Components/Toolkit/AddToCartOptionsAndButton";
import ShoppingCartLine from "@store/Library/Components/Toolkit/Icons/ShoppingCart/ShoppingCartLine";
import { productCardProps } from "@store/Themes/common/props/productCard";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import React, { FC, useMemo } from "react";

/* Store Custom Code - Start */
import translate from "@insite/client-framework/Translate";
import { getMultiLocationCatalogResult } from "@store/Framework/Common/Utilities/multiLocationCatalogUtilities";
import { useProductListProductContext } from "@store/Framework/Components/ProductListProductContext/ProductListProductContext";
import ProductFuturePrice from "@store/Library/Components/ProductDetails/ProductFuturePrice";
import RequestQuoteButton, { RequestQuoteButtonPresentationProps } from "@store/Library/Components/RequestQuoteButton";
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCallback } from "react";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
/* Store Custom Code - End */

const mobileBreakpoint: BreakpointKey = "xs";

const alternativePriceProps = {
    realTimeText: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.small};
        `,
    },
    priceText: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.small};
        `,
    },
};
/* Starter Custom Code - End */

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    // Starter Custom Code
    useAlternativeDisplayOnMobile?: boolean;
    // Store Custom Code
    showAddToCart: boolean;
    showBreakPricing: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss; // Starter Custom Code
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    // Starter Custom Code
    priceWrapper?: InjectableCss;
    addToCartOptionsAndButton?: AddToCartOptionsAndButtonStyles;
    // Store Custom Code
    genericButton?: ButtonPresentationProps;
    genericRfqButton?: RequestQuoteButtonPresentationProps;
    mlcButton?: ButtonPresentationProps;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: productCardProps.actionsWrapper,
    priceWrapper: productCardProps.spacingWrapper,
    price: productCardProps.price,
    quantityBreakPricing: productCardProps.quantityBreakPricing,
    addToCartOptionsAndButton: productCardProps.addToCartOptionsAndButton,
    addToListWrapper: productCardProps.addToListWrapper,
    addToListLink: productCardProps.addToListLink,
    /* Store Custom Code - Start */
    genericButton: {
        mergeCss: true,
        css: css`
            margin-top: ${pxToRem(20)};
        `,
    },
    genericRfqButton: {
        extendedStyles: {
            link: {
                css: css`
                    margin-top: ${pxToRem(16)};
                    justify-content: center;
                `,
            },
        },
    },
    mlcButton: {
        variant: "tertiary",
        mergeCss: true,
        css: css`
            margin-top: ${pxToRem(16)};
        `,
    },
    /* Store Custom Code - End */
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    // Starter Custom Code
    useAlternativeDisplayOnMobile,
    // Store Custom Code
    showAddToCart,
    showBreakPricing,
}) => {
    /* Starter Custom Code - Start */
    const canAddToList = showAddToList && productContext.product.canAddToWishlist;
    const isMobile = useIsBreakpoint(mobileBreakpoint, "down");

    const addToCartButtonStyle = useMemo(() => {
        if (isMobile && useAlternativeDisplayOnMobile) {
            return mergeProps(styles.addToCartOptionsAndButton?.addToCartButton, {
                showButtonIconOnly: true,
                icon: {
                    src: ShoppingCartLine,
                    position: "left",
                },
                extendedStyles: {
                    button: {
                        css: css`
                            min-width: initial;
                        `,
                    },
                },
            });
        }
        return styles.addToCartOptionsAndButton;
    }, [isMobile, useAlternativeDisplayOnMobile, styles.addToCartOptionsAndButton?.addToCartButton]);

    const priceStyles = useMemo(() => {
        if (isMobile && useAlternativeDisplayOnMobile) {
            return mergeProps<ProductListActionsStyles["price"]>(styles.price, {
                secondaryPrice: alternativePriceProps,
                price: alternativePriceProps,
            });
        }
        return styles.price;
    }, [isMobile, useAlternativeDisplayOnMobile, styles.price, alternativePriceProps]);
    /* Starter Custom Code - End */

    /* Store Custom Code - Start */
    const productListProductContext = useProductListProductContext();
    const handleAddToCartClick = useCallback(() => {
        productListProductContext?.onAddToCartClick?.(false);
    }, [productListProductContext?.onAddToCartClick]);

    if (getBooleanProperty(productContext.product.properties.genericProduct)) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Button {...styles.genericButton} href={productContext.productInfo.productDetailPath}>
                    {translate("Learn more")}
                </Button>
                <RequestQuoteButton
                    {...styles.genericRfqButton}
                    productId={productContext.product.id}
                    isButtonLink={true}
                    showIcon={false}
                />
            </StyledWrapper>
        );
    }

    const multiLocationCatalogResult = getMultiLocationCatalogResult(productContext.product);
    const isMLCProduct = Boolean(multiLocationCatalogResult?.isMultiLocationCatalogProduct);

    if (isMLCProduct) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Button {...styles.mlcButton} href={productContext.productInfo.productDetailPath}>
                    {translate("Pricing & Details")}
                </Button>
            </StyledWrapper>
        );
    }
    /* Store Custom Code - End */

    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice && (
                <StyledWrapper {...styles.priceWrapper /* Starter Custom Code */}>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={priceStyles} // Starter Custom Code
                    />
                    <ProductFuturePrice product={productContext} /* Store Custom Code */ />
                    {showBreakPricing /* Store Custom Code */ && (
                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                    )}
                </StyledWrapper>
            )}
            {showAddToCart /* Store Custom Code */ && (
                <AddToCartOptionsAndButton
                    extendedStyles={{
                        ...styles.addToCartOptionsAndButton,
                        unitOfMeasure: {
                            ...styles.addToCartOptionsAndButton?.unitOfMeasure,
                            uid: `uom-${productContext.product.id}`,
                        },
                        quantityOrdered: {
                            ...styles.addToCartOptionsAndButton?.quantityOrdered,
                            id: `qty-ordered-${productContext.product.id}`,
                        },
                        addToCartButton: {
                            ...addToCartButtonStyle, // Starter Custom Code
                            "data-test-selector": `actionsAddToCart${productContext.product.id}`,
                            onAddToCartClick: handleAddToCartClick, // Store Custom Code
                        },
                    }}
                />
            )}
            {canAddToList /* Starter Custom Code */ && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
